import React, { useState, useEffect, useMemo } from 'react';
import {
  FileSpreadsheet,
  Search,
  Calendar,
  CheckCircle2,
  Clock,
  XCircle,
  FileDown,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  Eye,
  Truck
} from 'lucide-react';
import { useAreaCliente } from '../../contexts/AreaClienteContext';
import { areaClienteService } from '../../services/areaClienteService';
import { Medicoes } from '../../types/medicao';
import { AreaClienteDetalhesMedicaoModal } from '../../components/areaCliente/AreaClienteDetalhesMedicaoModal';

export function AreaClienteMedicoes() {
  const { clienteAtual } = useAreaCliente();
  const [medicoes, setMedicoes] = useState<Medicoes[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMedicaoForDetails, setSelectedMedicaoForDetails] = useState<Medicoes | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const loadMedicoes = async () => {
    if (!clienteAtual) return;
    setIsLoading(true);
    try {
      const data = await areaClienteService.getMedicoesCliente(clienteAtual.id);
      setMedicoes(data);
    } catch (err) {
      console.error('Erro ao carregar medições do cliente:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMedicoes();
  }, [clienteAtual]);

  const formatCurrency = (val?: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(val || 0);
  };

  const getStatusBadge = (statusId?: number) => {
    switch (statusId) {
      case 1:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Aprovado
          </span>
        );
      case 2:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300 flex items-center gap-1">
            <XCircle className="w-3 h-3 text-red-600" /> Reprovado
          </span>
        );
      case 3:
      default:
        return (
          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1">
            <Clock className="w-3 h-3 text-amber-600" /> Em Análise
          </span>
        );
    }
  };

  const filteredMedicoes = useMemo(() => {
    return medicoes.filter(m => {
      if (statusFilter === 'aprovado' && m.status_medicao_id !== 1) return false;
      if (statusFilter === 'reprovado' && m.status_medicao_id !== 2) return false;
      if (statusFilter === 'analise' && m.status_medicao_id !== 3 && m.status_medicao_id !== null && m.status_medicao_id !== undefined) return false;

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchId = String(m.id).includes(term);
        const matchOs = String(m.ordem_de_servico_id || '').includes(term);
        if (!matchId && !matchOs) return false;
      }

      if (startDate && m.periodo_inicial && m.periodo_inicial < startDate) return false;
      if (endDate && m.periodo_final && m.periodo_final > endDate) return false;

      return true;
    });
  }, [medicoes, statusFilter, searchTerm, startDate, endDate]);

  const totalValor = useMemo(() => {
    return filteredMedicoes.reduce((acc, curr) => acc + (Number(curr.valor_medicao) || 0), 0);
  }, [filteredMedicoes]);

  const totalAprovadas = useMemo(() => {
    return medicoes.filter(m => m.status_medicao_id === 1).length;
  }, [medicoes]);

  const totalEmAnalise = useMemo(() => {
    return medicoes.filter(m => m.status_medicao_id === 3 || !m.status_medicao_id).length;
  }, [medicoes]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-blue-50 text-blue-600 rounded-xl">
            <FileSpreadsheet className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-black text-slate-900">Medições de Serviços</h1>
            <p className="text-xs text-slate-500">
              Consulte as apurações periódicas de transporte, valores consolidados e relatórios.
            </p>
          </div>
        </div>

        <button
          onClick={loadMedicoes}
          className="self-start sm:self-auto px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors text-xs font-semibold flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          <span>Atualizar</span>
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Total Apurado em Medições</span>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-black text-slate-900">{formatCurrency(totalValor)}</span>
            <span className="text-xs text-slate-400">{filteredMedicoes.length} registros</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Medições Aprovadas</span>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-black text-emerald-700">{totalAprovadas}</span>
            <span className="text-xs text-emerald-600 font-semibold">Liberadas para faturamento</span>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <span className="text-[11px] font-bold uppercase tracking-wider text-amber-600">Em Análise / Conferência</span>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="text-2xl font-black text-amber-700">{totalEmAnalise}</span>
            <span className="text-xs text-amber-600 font-semibold">Em validação periódica</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'todos', label: 'Todas as Medições', count: medicoes.length },
            { id: 'aprovado', label: 'Aprovadas', count: totalAprovadas },
            { id: 'analise', label: 'Em Análise', count: totalEmAnalise },
            { id: 'reprovado', label: 'Reprovadas', count: medicoes.filter(m => m.status_medicao_id === 2).length }
          ].map(tab => {
            const isActive = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center space-x-1.5 ${
                  isActive
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  isActive ? 'bg-slate-800 text-slate-200' : 'bg-slate-200 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por ID da medição ou OS..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Período Inicial"
            />
          </div>

          <div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Período Final"
            />
          </div>
        </div>
      </div>

      {/* Medições Table */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-3 text-xs text-slate-500">Carregando medições...</p>
          </div>
        ) : filteredMedicoes.length === 0 ? (
          <div className="py-16 text-center space-y-3 px-4">
            <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">Nenhuma medição encontrada</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              As medições periódicas são geradas e disponibilizadas aqui pela equipe operacional.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3 px-4">ID Medição</th>
                  <th className="py-3 px-4">Período Apurado</th>
                  <th className="py-3 px-4">Composição</th>
                  <th className="py-3 px-4">Valor Total</th>
                  <th className="py-3 px-4">Status da Medição</th>
                  <th className="py-3 px-4 text-center">Ações / Detalhes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMedicoes.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3.5 px-4 font-black text-slate-900">
                      #{m.id}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="font-semibold text-slate-800">
                        {m.periodo_inicial ? new Date(m.periodo_inicial + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                        {' até '}
                        {m.periodo_final ? new Date(m.periodo_final + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                      </div>
                    </td>
                    <td className="py-3.5 px-4">
                      <button
                        type="button"
                        onClick={() => setSelectedMedicaoForDetails(m)}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer"
                      >
                        <Truck className="w-3.5 h-3.5" />
                        <span>Ver Ordens de Serviço</span>
                      </button>
                    </td>
                    <td className="py-3.5 px-4 font-black text-slate-900 whitespace-nowrap">
                      {formatCurrency(m.valor_medicao)}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      {getStatusBadge(m.status_medicao_id)}
                    </td>
                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedMedicaoForDetails(m)}
                          className="inline-flex items-center space-x-1 px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-colors cursor-pointer"
                          title="Visualizar Detalhes e OSs"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          <span>Detalhes</span>
                        </button>
                        {m.arquivo && (
                          <a
                            href={m.arquivo}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center space-x-1 px-2.5 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg text-xs font-bold transition-colors"
                          >
                            <FileDown className="w-3.5 h-3.5" />
                            <span>PDF</span>
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Details Modal */}
      {selectedMedicaoForDetails && (
        <AreaClienteDetalhesMedicaoModal
          medicao={selectedMedicaoForDetails}
          onClose={() => setSelectedMedicaoForDetails(null)}
        />
      )}
    </div>
  );
}
